import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorBlock, Field, LoadingBlock, Modal, Notice, Palette, Panel,
  ReadOnlyHint, SectionHeader, SettingsSectionProps, apiError, btn, fmtDate, inputStyle, palette,
} from './settingsUi';

/**
 * Settings -> Financial data. A workspace can store its own Tracxn / Crunchbase credentials (encrypted, admin
 * only), but FluxWatch doesn't fetch financial data from them yet -- the backend answers 501 for test and sync
 * rather than faking them (see backend routes/financial_provider_routes.py). The page says so plainly.
 */

type ProviderName = 'tracxn' | 'crunchbase';

interface FinancialProvider {
  id: string;
  provider_name: ProviderName;
  is_active: boolean;
  key_hint: string | null;
  has_secret: boolean;
  last_sync?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface CatalogEntry {
  name: ProviderName;
  label: string;
  description: string;
  data: string[];
  keyHint: string;
  hasSecret: boolean;
}

const CATALOG: CatalogEntry[] = [
  {
    name: 'tracxn',
    label: 'Tracxn',
    description: "Funding data, investor information and company valuation from Tracxn's database.",
    data: ['Funding stage', 'Total raised', 'Investors', 'Valuation'],
    keyHint: 'From your Tracxn dashboard.',
    hasSecret: true,
  },
  {
    name: 'crunchbase',
    label: 'Crunchbase',
    description: "Funding rounds, acquisitions and company insights from Crunchbase's database.",
    data: ['Funding rounds', 'Total raised', 'Investors', 'Valuation'],
    keyHint: 'From the Crunchbase developer portal.',
    hasSecret: false,
  },
];
const catalogFor = (name: string) => CATALOG.find(p => p.name === name);
const labelFor = (name: string) => catalogFor(name)?.label ?? name;

const EMPTY_PROVIDERS: FinancialProvider[] = [];

export default function FinancialProviders({ isDark, isAdmin, notify }: SettingsSectionProps) {
  const c = palette(isDark);

  const providersQuery = useQuery({
    queryKey: ['settings', 'financial-providers', 'list'],
    queryFn: async () => (await api.get('/v1/settings/financial-providers')).data as {
      providers: FinancialProvider[]; live: boolean; status_message: string;
    },
  });
  const providers: FinancialProvider[] = providersQuery.data?.providers ?? EMPTY_PROVIDERS;

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/v1/settings/financial-providers', body),
    meta: { invalidates: [['settings', 'financial-providers'], ['settings', 'history']] },
  });
  const deleteMutation = useMutation({
    mutationFn: (providerId: string) => api.delete(`/v1/settings/financial-providers/${providerId}`),
    meta: { invalidates: [['settings', 'financial-providers'], ['settings', 'history']] },
  });

  const [editing, setEditing] = useState<{ entry: CatalogEntry; existing?: FinancialProvider } | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [touched, setTouched] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<FinancialProvider | null>(null);

  const connectedNames = new Set(providers.map(p => p.provider_name));
  const available = CATALOG.filter(p => !connectedNames.has(p.name));

  const openModal = (entry: CatalogEntry, existing?: FinancialProvider) => {
    setApiKey(''); setApiSecret(''); setShowKey(false); setShowSecret(false); setTouched(false);
    setEditing({ entry, existing });
  };
  const closeModal = () => { if (!saveMutation.isPending) setEditing(null); };
  const keyError = touched && !apiKey.trim() ? 'Enter the API key.' : undefined;

  const save = async () => {
    if (!editing) return;
    setTouched(true);
    if (!apiKey.trim()) return;
    const { entry } = editing;
    try {
      await saveMutation.mutateAsync({
        provider_name: entry.name, api_key: apiKey.trim(), ...(entry.hasSecret ? { api_secret: apiSecret.trim() } : {}),
      });
      notify(`${entry.label} credentials saved.`, 'ok');
      setEditing(null);
    } catch (e) {
      notify(apiError(e, `Couldn't save ${entry.label}.`), 'error');
    }
  };

  const runRemove = async () => {
    const p = confirmRemove;
    if (!p) return;
    try {
      await deleteMutation.mutateAsync(p.id);
      notify(`${labelFor(p.provider_name)} removed.`, 'ok');
      setConfirmRemove(null);
    } catch (e) {
      notify(apiError(e, "Couldn't remove the provider."), 'error');
    }
  };

  const header = (
    <SectionHeader c={c} title="Financial data"
      description="Funding, valuation and investor data for your vendors' financial-health checks, from your own Tracxn or Crunchbase subscription."
      actions={!isAdmin ? <ReadOnlyHint c={c}>Only admins can manage financial data providers.</ReadOnlyHint> : undefined} />
  );

  if (providersQuery.isPending) return <div>{header}<LoadingBlock c={c} rows={2} /></div>;
  if (providersQuery.isError) {
    return (
      <div>
        {header}
        <ErrorBlock c={c} message={apiError(providersQuery.error, "We couldn't load your financial data providers.")}
          onRetry={() => providersQuery.refetch()} />
      </div>
    );
  }

  const entry = editing?.entry;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {header}

      <Notice c={c} tone="warn" title="Coming soon">
        {providersQuery.data?.status_message || "FluxWatch doesn't fetch data from these providers yet."} No financial data is shown on vendors until then.
      </Notice>

      <Panel c={c} title="Saved credentials" flush
        badge={providers.length > 0 ? <Badge c={c}>{providers.length}</Badge> : undefined}
        description="Stored encrypted for this workspace only. The full key is never shown again.">
        {providers.length === 0 ? (
          <EmptyState c={c} title="No provider credentials saved"
            body={isAdmin ? 'Save your provider credentials now so data can start flowing as soon as the integration is released.'
              : 'An admin can add provider credentials.'} />
        ) : providers.map((p, i) => (
          <ProviderRow key={p.id} c={c} p={p} first={i === 0} isAdmin={isAdmin}
            onEdit={() => { const e = catalogFor(p.provider_name); if (e) openModal(e, p); }}
            onRemove={() => setConfirmRemove(p)} />
        ))}
      </Panel>

      {available.length > 0 && (
        <Panel c={c} title="Supported providers" description="Use your own subscription's API key.">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 12 }}>
            {available.map(p => (
              <div key={p.name} style={{
                border: `1px solid ${c.line}`, borderRadius: 10, padding: 16, background: c.surface2,
                display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <ProviderMark c={c} label={p.label} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: c.ink }}>{p.label}</span>
                  <Badge c={c} tone="warn">Coming soon</Badge>
                </div>
                <div style={{ fontSize: 12.5, color: c.slate, lineHeight: 1.5 }}>{p.description}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {p.data.map(d => <Badge key={d} c={c}>{d}</Badge>)}
                </div>
                <div style={{ marginTop: 'auto', paddingTop: 4 }}>
                  {isAdmin
                    ? <Button c={c} onClick={() => openModal(p)}>Save credentials</Button>
                    : <ReadOnlyHint c={c}>Only admins can add providers.</ReadOnlyHint>}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Modal c={c} open={!!editing} onClose={closeModal} closeOnBackdrop={!saveMutation.isPending}
        title={entry ? `${editing?.existing ? 'Replace' : 'Save'} ${entry.label} credentials` : ''}
        description={entry ? `Stored encrypted. Nothing is sent to ${entry.label} until the integration is released.` : undefined}
        footer={entry && <>
          <Button c={c} onClick={closeModal} disabled={saveMutation.isPending}>Cancel</Button>
          <Button c={c} kind="primary" onClick={save} busy={saveMutation.isPending} disabled={!apiKey.trim()}>Save</Button>
        </>}>
        {entry && (
          <form onSubmit={e => { e.preventDefault(); save(); }} style={{ display: 'grid', gap: 16 }}>
            <Field c={c} label="API key" error={keyError} hint={entry.keyHint}>
              <SecretInput c={c} value={apiKey} shown={showKey} onToggle={() => setShowKey(s => !s)}
                invalid={!!keyError} placeholder={`Your ${entry.label} API key`} autoFocus onChange={setApiKey} />
            </Field>
            {entry.hasSecret && (
              <Field c={c} label="API secret (optional)"
                hint={editing?.existing ? 'Leaving this blank clears any stored secret.' : 'Some Tracxn APIs require a secret.'}>
                <SecretInput c={c} value={apiSecret} shown={showSecret} onToggle={() => setShowSecret(s => !s)}
                  placeholder="Your Tracxn API secret" onChange={setApiSecret} />
              </Field>
            )}
            <button type="submit" hidden aria-hidden tabIndex={-1} />
          </form>
        )}
      </Modal>

      <ConfirmDialog c={c} open={!!confirmRemove} tone="danger" confirmLabel="Remove" busy={deleteMutation.isPending}
        title={confirmRemove ? `Remove ${labelFor(confirmRemove.provider_name)}?` : ''}
        onCancel={() => setConfirmRemove(null)} onConfirm={runRemove}
        body={confirmRemove && <>The stored {labelFor(confirmRemove.provider_name)} credentials are deleted. You can add them again at any time.</>} />
    </div>
  );
}

/* ─── Pieces ──────────────────────────────────────────────────────────────────────── */

function ProviderMark({ c, label }: { c: Palette; label: string }) {
  return (
    <span aria-hidden style={{
      width: 32, height: 32, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: c.accentBg, border: `1px solid ${c.accentBdr}`, color: c.accent, fontSize: 13, fontWeight: 700,
    }}>{label.charAt(0)}</span>
  );
}

function ProviderRow({ c, p, first, isAdmin, onEdit, onRemove }: {
  c: Palette; p: FinancialProvider; first: boolean; isAdmin: boolean; onEdit: () => void; onRemove: () => void;
}) {
  const label = labelFor(p.provider_name);
  const saved = p.updated_at || p.created_at;
  const meta = [p.key_hint ? `Key ${p.key_hint}` : 'Key saved', p.has_secret ? 'Secret saved' : null, saved ? `Saved ${fmtDate(saved)}` : null]
    .filter(Boolean) as string[];
  return (
    <div style={{
      padding: '14px 20px', borderTop: first ? 'none' : `1px solid ${c.softLine}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px 16px', flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: '1 1 260px' }}>
        <ProviderMark c={c} label={label} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{label}</span>
            <Badge c={c} tone="warn">Not fetching yet</Badge>
          </div>
          <div style={{ fontSize: 12, color: c.slate, marginTop: 3 }}>{meta.join(' · ')}</div>
        </div>
      </div>
      {isAdmin ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button c={c} onClick={onEdit}>Replace key</Button>
          <Button c={c} kind="danger" onClick={onRemove}>Remove</Button>
        </div>
      ) : <ReadOnlyHint c={c} />}
    </div>
  );
}

function SecretInput({ c, value, onChange, shown, onToggle, invalid, placeholder, autoFocus }: {
  c: Palette; value: string; onChange: (v: string) => void; shown: boolean; onToggle: () => void;
  invalid?: boolean; placeholder?: string; autoFocus?: boolean;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <input type={shown ? 'text' : 'password'} value={value} placeholder={placeholder} autoFocus={autoFocus}
        autoComplete="off" spellCheck={false} onChange={e => onChange(e.target.value)}
        style={{ ...inputStyle(c, invalid), paddingRight: 64 }} />
      <button type="button" onClick={onToggle} aria-label={shown ? 'Hide value' : 'Show value'}
        style={{ ...btn(c, 'ghost'), position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', padding: '4px 8px', fontSize: 12 }}>
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}
